import { HttpStatusCode } from 'axios'
import { parseISO } from 'date-fns'
import { DrilldownExploration } from 'features/drilldown/components/settings/DrilldownExplorationPicker'
import { useDefaultFilters } from 'features/drilldown/data/defaultFilters'
import {
  DEFAULT_EMPTY_CONFIGURATION,
  DEFAULT_EMPTY_VIEW_STATE,
} from 'features/drilldown/data/savedExplorationState/DrilldownExplorationContext'
import {
  extractHistoResult,
  histogramSelect,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import {
  DrilldownExplorationConfiguration,
  DrilldownPanelState,
  DrilldownViewState,
} from 'features/drilldown/types'
import {
  OntologyRolodexEntryType,
  useHistogramQueries,
  useOntology,
} from 'features/dynamicOntology'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import {
  RolodexEntryQueryBody,
  useRolodexEntries,
  useRolodexHistogram,
} from 'features/rolodex'
import debounce from 'lodash/debounce'
import findIndex from 'lodash/findIndex'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import reduce from 'lodash/reduce'
import sum from 'lodash/sum'
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import {
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
  RolodexHistogramFilter,
} from 'types'
import { parseString } from 'utils/parsingHelpers'

type EntryPutJson = {
  id: string
  type: RolodexEntryType
  data: {
    userId: string
    viewState: ReadonlyArray<
      Omit<DrilldownPanelState, 'filters'> & {
        filters: Record<string, ReadonlyArray<RolodexHistogramFilter>>
      }
    >
    name: string
    configuration?: DrilldownExplorationConfiguration
  } & Pick<RolodexDrilldownExplorationState, 'name' | 'published'>
  tags: RolodexEntryTagsType
}

const RD_TYPE = RolodexEntryType.DRILLDOWN_EXPLORATION
const UNIDENTIFIED_USER = 'unidentifiedUser'
export const DEFAULT_EXPLORATION_NAME = 'Unnamed'

export interface RolodexDrilldownExplorationState {
  id?: string
  orgId: string | null
  siteId: string | null
  name: string
  viewState: DrilldownViewState
  configuration: DrilldownExplorationConfiguration
  published: boolean
}

const EMPTY_RD_DRILLDOWN_STATE = {
  orgId: null,
  siteId: null,
  name: DEFAULT_EXPLORATION_NAME,
  viewState: DEFAULT_EMPTY_VIEW_STATE,
  configuration: DEFAULT_EMPTY_CONFIGURATION,
  presentationMode: false,
  published: false,
}

function mapToRec<K extends string, V>(map: ReadonlyMap<K, V>): Record<K, V> {
  const obj: Record<K, V> = {} as Record<K, V>

  // Convert Map to Record
  map.forEach((value, key) => {
    obj[key] = value
  })

  return obj
}

function toRolodexEntry(
  rdState: RolodexDrilldownExplorationState,
  tags: RolodexEntryTagsType,
  userId: string
): EntryPutJson | undefined {
  return rdState.id
    ? {
        id: rdState.id,
        type: RD_TYPE,
        data: {
          ...pick(rdState, 'name', 'orgId', 'siteId'),
          viewState: rdState.viewState.map((s) => ({
            ...s,
            filters: mapToRec<string, ReadonlyArray<RolodexHistogramFilter>>(
              s.filters
            ),
          })),
          configuration: rdState.configuration,
          userId,
          published: rdState.published,
        },
        tags,
      }
    : undefined
}

function fromRolodexEntry(
  entry: RolodexEntryJson
): RolodexDrilldownExplorationState {
  const viewStateRecs = entry.data['viewState'] as
    | EntryPutJson['data']['viewState']
    | undefined
  const viewState = viewStateRecs?.map((vs) => ({
    ...vs,
    focusItems: undefined,
    filters: new Map(Object.entries(vs.filters ?? {})),
  }))
  return {
    id: entry.id,
    orgId: entry.data['orgId'] as string | null,
    siteId: entry.data['siteId'] as string | null,
    name: parseString(entry.data.name) ?? DEFAULT_EXPLORATION_NAME,
    viewState: viewState ?? DEFAULT_EMPTY_VIEW_STATE,
    configuration:
      (entry.data.configuration as DrilldownExplorationConfiguration) ??
      DEFAULT_EMPTY_CONFIGURATION,
    published: (entry.data['published'] as boolean) ?? false,
  }
}

function isPanelPinned(data: object) {
  if ('isPinned' in data) {
    return data.isPinned === true
  }
  return false
}

/**
 * This is used to figure out if you only have 1 entry of type
 * which makes it through the supplied filters.
 *
 * It's there to figure out how to tag your exploration entry.
 *
 * @param type the type to count
 * @param filters the filters to apply
 * @returns the id of the entry that made it through, undefined if there wasn't exactly 1
 */
function useSingletonId(
  type: OntologyRolodexEntryType,
  filters: RolodexHistogramFilter[]
): { id: string | undefined; isLoading: boolean } {
  const { findLowestType } = useOntology()
  const lowest = useMemo(
    () =>
      findLowestType([
        type,
        ...filters
          .map((f) => f.type)
          .filter((item): item is OntologyRolodexEntryType => item !== null),
      ]) ?? type,
    [filters, findLowestType, type]
  )
  const {
    queries: [histoQuery],
    isLoading: queryLoading,
  } = useHistogramQueries({
    select: useMemo(() => histogramSelect(type, lowest), [lowest, type]),
    filters: filters,
  })
  const { data: _data, isLoading } = useRolodexHistogram(histoQuery)
  const data = useMemo(() => _data?.flat(), [_data])

  const count = useMemo(
    () =>
      findLowestType(['organization', type]) === 'organization'
        ? Object.keys(extractHistoResult(data)).length
        : sum(Object.values(extractHistoResult(data))),
    [data, findLowestType, type]
  )
  const firstId = (data?.[0]?.['group_id'] as string) ?? undefined
  return useMemo(
    () => ({
      id: count === 1 && firstId ? firstId : undefined,
      isLoading: isLoading || queryLoading,
    }),
    [count, firstId, isLoading, queryLoading]
  )
}

/**
 * Figure out what tags to apply to a Rolodex exploration entry.
 *
 * It will apply all filters up to and including the first pinned panel,
 * and apply tags for any type for which there is only 1 matching entry,
 * including organization, site and unit.
 *
 * @param viewState the viewState to compute tags for
 * @returns the tags to add to the entry
 */
function useComputeTags(
  viewState: DrilldownViewState | undefined,
  orgId: string | null,
  siteId: string | null
): {
  isLoading: boolean
  tags: RolodexEntryTagsType
} {
  const defaultFilters = useDefaultFilters(orgId, siteId)
  const filters = useMemo(() => {
    const minPinnedIdx = findIndex(viewState, ({ panelData: data }) =>
      isPanelPinned(data)
    )
    return [
      ...defaultFilters,
      ...(viewState ?? [])
        .slice(0, minPinnedIdx)
        .flatMap(({ filters: fs }) => (fs.size ? [...fs.values()] : []))
        .flat(),
    ]
  }, [defaultFilters, viewState])

  const { id: singleOrgId, isLoading: orgsLoading } = useSingletonId(
    'organization',
    filters
  )
  const { id: singleSiteId, isLoading: sitesLoading } = useSingletonId(
    'site',
    filters
  )
  const { id: singleUnitId, isLoading: unitsLoading } = useSingletonId(
    'unit',
    filters
  )

  const isLoading = !!reduce(
    [orgsLoading, sitesLoading, unitsLoading],
    (boolOne, boolTwo) => boolOne || boolTwo
  )

  const tags = useMemo(
    () =>
      Object.fromEntries(
        [
          ['organization', singleOrgId],
          ['site', singleSiteId],
          ['unit', singleUnitId],
        ].filter(([, value]) => !!value)
      ),
    [singleOrgId, singleSiteId, singleUnitId]
  )

  return useMemo(() => {
    return {
      isLoading,
      tags,
    }
  }, [isLoading, tags])
}

/**
 * Create a function which saves an exploration to Rolodex.
 * @returns a promise which completes when the PUT is complete
 */
export function useSaveExplorationToRolodex(): (
  state: RolodexDrilldownExplorationState,
  tags?: RolodexEntryTagsType
) => Promise<void> | undefined {
  const { rolodexPut } = useRolodexEdit()
  const { user: currentUser } = useGeckoPermissions()
  const { errorNotification } = useGeckoNotifications()

  return useMemo<
    (state: RolodexDrilldownExplorationState) => Promise<void> | undefined
  >(
    () =>
      debounce(
        async (
          state: RolodexDrilldownExplorationState,
          tags?: RolodexEntryTagsType
        ) => {
          /**
           * We rely on the panels to generate filters, so we opt not to write filters to the database, only the panel state.
           */
          const stateWithoutFilters: RolodexDrilldownExplorationState = {
            ...state,
            viewState: state.viewState.map(
              (panelState): DrilldownPanelState => ({
                ...panelState,
                filters: new Map(),
                focusItems: undefined,
              })
            ),
          }

          const putBody = toRolodexEntry(
            stateWithoutFilters,
            tags ?? {},
            currentUser?.id ?? 'unidentifiedUser'
          )
          if (putBody) {
            const axiosResponse = await rolodexPut([putBody])
            if (axiosResponse.status !== HttpStatusCode.Ok) {
              errorNotification(
                `Error saving to Rolodex: ${axiosResponse.statusText}`
              )
            }
          }
        },
        200
      ),
    [currentUser?.id, errorNotification, rolodexPut]
  )
}

export function useExplorationFromRolodex(rdId: string | undefined): {
  data: RolodexDrilldownExplorationState | undefined
  isLoading: boolean
} {
  const { entries, isLoading } = useRolodexEntries(
    useMemo(() => ({ ids: [rdId ?? 'null-id'] }), [rdId])
  )

  const { data, id } = useMemo(() => {
    if (!entries.length) {
      return { data: undefined, id: undefined }
    }
    return { data: fromRolodexEntry(entries[0]), id: entries[0].id }
  }, [entries])

  return useMemo(
    () => ({ data: id === rdId ? data : undefined, isLoading, id }),
    [data, id, isLoading, rdId]
  )
}

/**
 * The main entry point, this returns a RolodexDrilldownExplorationState
 * which represents what is in Rolodex. Calls to the setter update local state,
 * and are written through to Rolodex.
 * @param id the ID of the record to read from / write to
 * @returns the state, setter and isLoading flag
 */
export function useSyncDrilldownStateToRolodex(id: string | undefined): {
  state: RolodexDrilldownExplorationState
  setState: Dispatch<SetStateAction<RolodexDrilldownExplorationState>>
  clearState: (orgId: string | null, siteId: string | null) => void
  isLoading: boolean
} {
  const [state, _setState] = useState<RolodexDrilldownExplorationState>(
    EMPTY_RD_DRILLDOWN_STATE
  )

  const { tags, isLoading: tagsLoading } = useComputeTags(
    state?.viewState,
    state.orgId,
    state.siteId
  )

  const saveToRolodex = useSaveExplorationToRolodex()
  const doUpdate = useCallback(
    async (s: RolodexDrilldownExplorationState) => {
      saveToRolodex(s, tags)
    },
    [saveToRolodex, tags]
  )

  // Update rolodex on fresh state
  const setState = useCallback(
    (
      val:
        | RolodexDrilldownExplorationState
        | ((
            prev: RolodexDrilldownExplorationState
          ) => RolodexDrilldownExplorationState)
    ) => {
      _setState((prev) => {
        const newVal = typeof val === 'function' ? val(prev) : val
        if (isEqual(newVal, prev)) {
          return prev
        }
        if (id && newVal && newVal.id === id) {
          doUpdate(newVal)
        }
        return newVal
      })
    },
    [doUpdate, id]
  )

  const clearState = useCallback(
    (orgId: string | null, siteId: string | null) => {
      setState({ ...EMPTY_RD_DRILLDOWN_STATE, siteId, orgId })
    },
    [setState]
  )

  return useMemo(
    () => ({
      state,
      setState,
      isLoading: !!id && tagsLoading,
      clearState,
    }),
    [clearState, id, setState, state, tagsLoading]
  )
}

/**
 * Helper function to load the Rolodex drilldown exploration states
 * which were created by the currently logged in user.
 * @returns the explorations for the current user
 */
export function useRolodexExplorationsForUser(
  limitToCurrentUser?: boolean,
  tags?: RolodexEntryQueryBody['tags'],
  publishedOnly?: boolean
): {
  explorations: ReadonlyArray<DrilldownExploration>
  isLoading: boolean
  forceRefresh: () => Promise<void>
} {
  const { user: currentUser } = useGeckoPermissions()
  const userIds = useMemo(
    () => (currentUser ? [currentUser.id] : []),
    [currentUser]
  )
  const query = useMemo<RolodexEntryQueryBody>(
    () => ({
      type: RD_TYPE,
      filters: [
        limitToCurrentUser === true || limitToCurrentUser === undefined
          ? {
              key: 'userId',
              op: 'in',
              value: [UNIDENTIFIED_USER, ...userIds],
            }
          : undefined,
        publishedOnly
          ? {
              key: 'published',
              op: 'truthy',
            }
          : undefined,
      ].filter((item): item is NonNullable<typeof item> => !!item),
      tags: tags ?? {},
    }),
    [limitToCurrentUser, publishedOnly, tags, userIds]
  )
  const {
    entries: explorations,
    isLoading,
    forceRefresh,
  } = useRolodexEntries(query)
  return useMemo(
    () => ({
      explorations: explorations.map((e) => ({
        id: e.id,
        name: parseString(e.data.name) ?? DEFAULT_EXPLORATION_NAME,
        organizationId: e.data.orgId as string | null,
        siteId: e.data.siteId as string | null,
        published: (e.data.published as boolean) ?? false,
        updatedAt: parseISO(e.raw.updated_at),
      })),
      isLoading,
      forceRefresh,
    }),
    [explorations, forceRefresh, isLoading]
  )
}
